import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, Package, Clock } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("polling");
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data.order);
          setState("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
      } catch (e) { console.error("Payment status poll failed", e); }
      attempts += 1;
      if (attempts > 15) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId, clear]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center">
      {state === "polling" && (
        <div data-testid="payment-polling"><Loader2 className="animate-spin mx-auto text-[#ff6a13]" size={44} /><p className="text-zinc-400 mt-4 font-mono-p">Confirming your payment…</p></div>
      )}
      {(state === "error" || state === "timeout") && (
        <div data-testid="payment-error"><p className="text-zinc-300">We couldn't confirm your payment yet. If you were charged, your order will appear in your account shortly.</p>
          <Link to="/account"><Button className="mt-6 bg-[#ff6a13] hover:bg-[#ff8a3d] rounded-full">Go to My Orders</Button></Link></div>
      )}
      {state === "paid" && order && (
        <div className="animate-fade-up" data-testid="payment-success">
          <CheckCircle2 className="mx-auto text-[#10b981]" size={56} />
          <h1 className="font-display text-3xl font-extrabold text-white mt-4">Payment confirmed!</h1>
          <p className="text-zinc-400 mt-2">Your order is now queued for production.</p>
          <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#22c3d6] bg-[#22c3d6]/10 border border-[#22c3d6]/30 rounded-full px-4 py-2 font-mono-p" data-testid="turnaround-badge"><Clock size={14} /> Estimated 48–72 hr production turnaround</div>
          <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-6 mt-8 text-left">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-zinc-500 font-mono-p uppercase">Order Number</p><p className="font-display text-2xl font-bold text-white font-mono-p">{order.order_number}</p></div>
              <Package className="text-[#ff6a13]" size={28} />
            </div>
            <div className="mt-5 space-y-2">
              {order.items.map((it, i) => (
                <div key={`${it.name}-${i}`} className="flex items-center gap-3 bg-[#1e222d] border border-zinc-800 rounded-xl p-3">
                  <div className="w-12 h-12 checker-light rounded-lg overflow-hidden flex items-center justify-center shrink-0">
                    {(it.image_url || it.preview_url) && <img src={fileUrl(it.preview_url || it.image_url)} alt="" className="max-w-full max-h-full object-contain" />}
                  </div>
                  <div className="flex-1 min-w-0"><p className="text-sm text-white truncate">{it.name}</p><p className="text-xs text-zinc-500 font-mono-p">Qty {it.quantity}</p></div>
                  <span className="text-sm font-mono-p text-zinc-300">${(it.unit_price * it.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            {order.requires_deposit ? (
              <div className="border-t border-zinc-800 mt-4 pt-4 space-y-1.5" data-testid="success-deposit">
                <div className="flex justify-between text-sm"><span className="text-zinc-400">Order total</span><span className="font-mono-p text-zinc-300">${order.total.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Deposit paid (50%)</span><span className="font-bold font-mono-p text-[#10b981]">${(order.deposit_amount || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Balance due on delivery</span><span className="font-bold font-mono-p text-white">${(order.balance_due || 0).toFixed(2)}</span></div>
                <p className="text-xs text-zinc-500 pt-1 leading-relaxed">We'll be in touch to confirm your delivery date and collect the remaining balance.</p>
              </div>
            ) : (
              <div className="flex justify-between border-t border-zinc-800 mt-4 pt-4"><span className="text-zinc-400">Total paid</span><span className="font-bold text-lg font-mono-p text-white">${order.total.toFixed(2)}</span></div>
            )}
          </div>
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/account"><Button className="bg-[#ff6a13] hover:bg-[#ff8a3d] rounded-full font-semibold">My Orders</Button></Link>
            <Link to="/catalog"><Button variant="outline" className="rounded-full border-zinc-700 bg-transparent text-white hover:bg-zinc-900">Keep Shopping</Button></Link>
          </div>
        </div>
      )}
    </div>
  );
}
